import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  ChevronLeft, 
  RefreshCw, 
  Search, 
  Trash2, 
  Archive,
  AlertCircle
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { useData } from "../../context/DataContext";
import { useAuth } from "../../context/AuthContext";
import { useHierarchy } from "../../hooks/useHierarchy";
import api from "../../utils/api";
import { toast } from "sonner";
import { formatDate } from "../../utils/qualification-utils";
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import { ShieldAlert } from "lucide-react";

export default function RecycleBinPage() {
  const navigate = useNavigate();
  const { restoreQualification } = useData();
  const { currentUser } = useAuth();
  const { canManageUser } = useHierarchy();
  const ability = useAbility(AbilityContext);
  const [deletedQualifications, setDeletedQualifications] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchDeleted = async () => {
    setIsLoading(true);
    try {
      const data = await api.get("/api/qualifications/recycle-bin");
      if (data.success) {
        setDeletedQualifications(data.qualifications || []);
      }
    } catch (error) {
      console.error("Error fetching recycle bin:", error);
      toast.error("Failed to load deleted records");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDeleted();
  }, []);

  const handleRestore = async (id: string) => {
    try {
      const success = await restoreQualification(id);
      if (success) {
        setDeletedQualifications(prev => (prev || []).filter(q => q.id !== id));
      }
    } catch (error) {
      console.error("Restore error:", error);
      toast.error("Failed to restore qualification");
    }
  };

  const filtered = (deletedQualifications || []).filter(q => 
    (q.name || "").toLowerCase().includes(searchTerm.toLowerCase()) || 
    (q.person?.name || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!ability.can(ACTIONS.READ, RESOURCES.QUALIFICATION)) {
    return (
      <div className="container mx-auto py-20 flex justify-center">
        <Card className="border-slate-200 shadow-lg p-12 text-center bg-white max-w-2xl">
          <div className="flex flex-col items-center justify-center space-y-4">
            <div className="bg-red-50 p-4 rounded-full">
              <ShieldAlert className="h-12 w-12 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900">Access Denied</h2>
            <p className="text-slate-500 max-w-md mx-auto">
              You do not have the required permissions to access the Qualifications Recycle Bin. 
              Please contact your system administrator if you believe this is an error.
            </p>
            <Button variant="outline" onClick={() => navigate("/qualifications")}>Back to Register</Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-slate-500 mb-2">
            <Button 
                variant="ghost" 
                size="sm" 
                className="p-0 h-auto hover:bg-transparent -ml-1 text-slate-500"
                onClick={() => navigate(-1)}
            >
                <ChevronLeft size={16} />
                <span>Back to Register</span>
            </Button>
          </div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Archive className="text-slate-400" size={24} />
            Recycle Bin
          </h1>
          <p className="text-slate-500">
            Restore soft-deleted qualification records within 180 days.
          </p>
        </div>
      </div>

      <Card className="border-slate-100 shadow-sm overflow-hidden">
        <CardHeader className="bg-slate-50/50 border-b border-slate-100 py-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input 
              placeholder="Search deleted records by name or staff..." 
              className="pl-9 bg-white" 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/30">
                <TableHead className="py-3 font-semibold text-slate-900">Qualification / Staff</TableHead>
                <TableHead className="py-3 font-semibold text-slate-900">Deleted At</TableHead>
                <TableHead className="py-3 font-semibold text-slate-900">Original Owner</TableHead>
                <TableHead className="text-right py-3 font-semibold text-slate-900">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={4} className="h-48 text-center text-slate-400">
                    <div className="flex flex-col items-center gap-2">
                      <RefreshCw className="animate-spin h-6 w-6" />
                      <span>Loading deleted records...</span>
                    </div>
                  </TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="h-64 text-center">
                    <div className="flex flex-col items-center gap-3 text-slate-400">
                      <div className="bg-slate-50 p-4 rounded-full">
                        <AlertCircle size={32} />
                      </div>
                      <div className="space-y-1">
                        <p className="text-slate-600 font-medium">No deleted records found</p>
                        <p className="text-sm">Restore records from here to have them reappear in the main register.</p>
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((q) => (
                  <TableRow key={q.id} className="hover:bg-slate-50 transition-colors">
                    <TableCell>
                      <div className="font-medium text-slate-900">{q.name}</div>
                      <div className="text-xs text-slate-500">{q.qualificationType?.name || "No Type"}</div>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">
                      {formatDate(q.deletedAt)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-[10px] font-bold text-slate-600">
                          {q.person?.name?.split(' ').map((n: any) => n[0]).join('')}
                        </div>
                        <span className="text-sm text-slate-700 font-medium">{q.person?.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      {(canManageUser(q.person?.id) || ability.can(ACTIONS.RESTORE, RESOURCES.QUALIFICATION)) && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="text-indigo-600 border-indigo-100 hover:bg-indigo-50 hover:text-indigo-700 gap-1.5"
                          onClick={() => handleRestore(q.id)}
                        >
                          <RefreshCw size={14} />
                          Restore
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
